/**
 * Admin de eventos del calendario — feriados, cierres de empresa,
 * recordatorios globales. Por ahora gestionamos solo los globales
 * (userId IS NULL); los personales los crea cada usuario desde
 * /calendar (TODO).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Trash2, Pencil, Plus, CalendarRange } from "lucide-react";
import {
  calendarEventCreateSchema,
  CALENDAR_EVENT_KINDS,
  CALENDAR_RECURRENCES,
  type CalendarEventCreateInput,
  type CalendarEventKind,
  type CalendarEventRow,
  type CalendarRecurrence,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/admin/calendar-events")({
  component: AdminCalendarEventsPage,
});

const KIND_LABEL: Record<CalendarEventKind, string> = {
  holiday: "Feriado",
  company_closure: "Cierre de empresa",
  meeting: "Junta",
  call: "Llamada",
  reminder: "Recordatorio",
  external: "Externo",
};

const KIND_BADGE: Record<CalendarEventKind, string> = {
  holiday: "badge-warning",
  company_closure: "badge-error",
  meeting: "badge-info",
  call: "badge-primary",
  reminder: "badge-ghost",
  external: "badge-ghost",
};

function AdminCalendarEventsPage() {
  const qc = useQueryClient();
  // null = cerrado; "new" = crear; row = editar.
  const [dialog, setDialog] = useState<"new" | CalendarEventRow | null>(null);

  const { data, isLoading } = useQuery<{ events: CalendarEventRow[] }>({
    queryKey: ["calendar-events", "global"],
    queryFn: () => api.get("/calendar-events?scope=global"),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/calendar-events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar-events"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administración"
        title="Eventos del calendario"
        subtitle="Feriados, cierres de empresa y recordatorios globales. Aparecen en el calendario de todos."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <button
              type="button"
              onClick={() => setDialog("new")}
              className="btn btn-primary btn-sm"
            >
              <Plus className="size-4" /> Nuevo evento
            </button>
          </div>
        }
      />

      <div className="card-elevated overflow-hidden">
        {isLoading ? (
          <div className="grid place-items-center py-16">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        ) : (data?.events ?? []).length === 0 ? (
          <div className="py-12 text-center">
            <CalendarRange className="size-10 mx-auto mb-3 text-base-content/30" />
            <p className="text-sm text-base-content/55">
              Aún no hay eventos globales. Click en "Nuevo evento" para agregar el primero.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-base-300/40">
            {(data?.events ?? []).map((e) => (
              <li key={e.id} className="px-4 py-3 flex items-center gap-3 hover:bg-base-200/40">
                <span
                  className={`badge badge-sm ${KIND_BADGE[e.kind as CalendarEventKind]} shrink-0`}
                >
                  {KIND_LABEL[e.kind as CalendarEventKind]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{e.name}</div>
                  <div className="text-xs text-base-content/55">
                    {e.startDate === e.endDate
                      ? formatDate(e.startDate)
                      : `${formatDate(e.startDate)} → ${formatDate(e.endDate)}`}
                    {e.recurrence && (
                      <span className="ml-2 text-primary">
                        🔁{" "}
                        {(
                          {
                            daily: "Diaria",
                            weekly: "Semanal",
                            biweekly: "Cada 2 sem.",
                            monthly: "Mensual",
                            yearly: "Anual",
                          } as Record<string, string>
                        )[e.recurrence] ?? e.recurrence}
                        {e.recurrenceUntil ? ` · hasta ${formatDate(e.recurrenceUntil)}` : ""}
                      </span>
                    )}
                    {e.blocksWork && <span className="ml-2 text-warning">· no laboral</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDialog(e)}
                  className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-primary"
                  aria-label="Editar"
                  title="Editar"
                >
                  <Pencil className="size-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`¿Borrar "${e.name}"?`)) del.mutate(e.id);
                  }}
                  className="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-error"
                  aria-label="Eliminar"
                  title="Eliminar"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialog !== null && (
        <EventDialog
          key={dialog === "new" ? "new" : dialog.id}
          event={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function EventDialog({ event, onClose }: { event: CalendarEventRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!event;
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CalendarEventCreateInput>({
    resolver: zodResolver(calendarEventCreateSchema),
    defaultValues: event
      ? {
          kind: event.kind,
          blocksWork: event.blocksWork,
          startDate: event.startDate,
          endDate: event.endDate,
          name: event.name,
          notes: event.notes ?? undefined,
          userId: event.userId,
          externalMeetUrl: event.externalMeetUrl ?? undefined,
          recurrence: event.recurrence,
          recurrenceDays: event.recurrenceDays,
          recurrenceUntil: event.recurrenceUntil,
        }
      : {
          kind: "holiday",
          blocksWork: true,
          startDate: new Date().toISOString().slice(0, 10),
          endDate: new Date().toISOString().slice(0, 10),
          name: "",
          userId: null,
          recurrence: null,
          recurrenceDays: null,
        },
  });

  const save = useMutation({
    mutationFn: (d: CalendarEventCreateInput) =>
      isEdit
        ? api.patch(`/calendar-events/${event.id}`, d)
        : api.post<{ id: string }>("/calendar-events", d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      onClose();
    },
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const recurrence = watch("recurrence");
  const externalId = watch("externalId");
  // Toggle "Basar en evento recurrente de Google". Visible solo cuando
  // estamos creando (en edit el anclaje ya está definido y cambiarlo
  // sería confuso — para reanclar, mejor borrar y crear otra vez).
  const [anchorMode, setAnchorMode] = useState<boolean>(!!event?.externalId);
  // "Evento de varios días": el usuario marca este toggle si la ocurrencia
  // dura más de un día (ej. cierre por vacaciones del 22 al 26 dic). Para
  // recurrentes single-day (cada lunes, etc.) Fin se auto-iguala a Inicio
  // y se esconde para no confundir con "hasta cuándo se repite".
  const [multiDay, setMultiDay] = useState(!!event && event.startDate !== event.endDate);
  useEffect(() => {
    if (!multiDay) setValue("endDate", startDate);
  }, [multiDay, startDate, setValue]);
  // Sincroniza endDate cuando cambia startDate y NO es multi-día.
  useEffect(() => {
    if (!multiDay) setValue("endDate", startDate);
  }, [startDate, multiDay, setValue]);
  void endDate;

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box card-elevated">
        <h3 className="font-display font-semibold text-lg mb-4">
          {isEdit ? `Editar: ${event.name}` : "Nuevo evento"}
        </h3>
        <form
          onSubmit={handleSubmit((d) =>
            save.mutate({
              ...d,
              endDate: d.endDate || d.startDate,
            }),
          )}
          className="space-y-3"
        >
          {!isEdit && (
            <div className="rounded-md border border-base-300/40 bg-base-200/20 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={anchorMode}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setAnchorMode(on);
                    if (!on) {
                      setValue("externalId", undefined);
                    } else {
                      // Anclaje siempre arranca como junta.
                      setValue("kind", "meeting");
                      setValue("blocksWork", false);
                    }
                  }}
                />
                <span className="text-sm font-medium">Basar en evento recurrente de Google</span>
              </label>
              <p className="text-xs text-base-content/55 mt-1">
                Útil si la junta ya existe como serie en tu calendario. El panel se ancla al master
                y rellena recurrencia, horario y link de Meet.
              </p>
              {anchorMode && (
                <GoogleRecurringPicker
                  value={externalId ?? null}
                  onSelect={(m) => {
                    setValue("externalId", m.id);
                    if (m.icalUid) setValue("externalIcalUid", m.icalUid);
                    setValue("name", m.summary);
                    setValue("startDate", m.startDate);
                    setValue("kind", "meeting");
                    setValue("blocksWork", false);
                    if (m.meetUrl) setValue("externalMeetUrl", m.meetUrl);
                    if (m.htmlLink) setValue("externalUrl", m.htmlLink);
                    if (m.mapped) {
                      setValue("recurrence", m.mapped.recurrence);
                      setValue("recurrenceDays", m.mapped.recurrenceDays);
                      setValue("recurrenceUntil", m.mapped.recurrenceUntil);
                    }
                  }}
                />
              )}
            </div>
          )}

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Nombre</span>
            </label>
            <input
              className="input input-bordered w-full"
              placeholder="Ej. Día de la Independencia"
              autoFocus
              {...register("name")}
            />
            {errors.name && (
              <span className="text-xs text-error mt-1 block">{errors.name.message}</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Tipo</span>
              </label>
              <select
                className="select select-bordered w-full"
                {...register("kind")}
                onChange={(e) => {
                  const v = e.target.value as CalendarEventKind;
                  setValue("kind", v);
                  // Ajuste automático: feriados/cierres bloquean por default.
                  setValue("blocksWork", v === "holiday" || v === "company_closure");
                }}
              >
                {CALENDAR_EVENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  {...register("blocksWork")}
                />
                <span className="text-sm">Cuenta como día no laboral</span>
              </label>
            </div>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">
                {recurrence ? "Fecha de inicio" : "Fecha"}
              </span>
            </label>
            <input type="date" className="input input-bordered w-full" {...register("startDate")} />
            <div className="mt-1">
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-base-content/65">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={multiDay}
                  onChange={(e) => setMultiDay(e.target.checked)}
                />
                Es un evento de varios días
              </label>
            </div>
          </div>

          {multiDay && (
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Fecha de fin</span>
              </label>
              <input
                type="date"
                className="input input-bordered w-full"
                min={startDate}
                {...register("endDate")}
              />
              <p className="text-xs text-base-content/55 mt-1">
                {recurrence
                  ? "Cada repetición durará desde el día de inicio hasta este día."
                  : "Día final del evento (inclusive)."}
              </p>
              {errors.endDate && (
                <span className="text-xs text-error mt-1 block">{errors.endDate.message}</span>
              )}
            </div>
          )}

          <RecurrenceFields
            recurrence={watch("recurrence")}
            recurrenceDays={watch("recurrenceDays")}
            recurrenceUntil={watch("recurrenceUntil")}
            onRecurrenceChange={(v) => {
              setValue("recurrence", v);
              if (!v || v === "monthly" || v === "yearly") setValue("recurrenceDays", null);
            }}
            onDaysChange={(days) => setValue("recurrenceDays", days)}
            onUntilChange={(v) => setValue("recurrenceUntil", v || null)}
          />

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Link de videoconferencia</span>
            </label>
            <input
              type="url"
              className="input input-bordered w-full"
              placeholder="https://meet.google.com/abc-defg-hij"
              {...register("externalMeetUrl")}
            />
            <p className="text-xs text-base-content/55 mt-1">
              Opcional. Se muestra como botón "Unirse a Meet" en el dashboard cuando el evento esté
              próximo.
            </p>
            {errors.externalMeetUrl && (
              <span className="text-xs text-error mt-1 block">
                {errors.externalMeetUrl.message}
              </span>
            )}
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Notas</span>
            </label>
            <textarea
              rows={2}
              className="textarea textarea-bordered w-full"
              placeholder="Opcional"
              {...register("notes")}
            />
          </div>

          <p className="text-xs text-base-content/55">
            Quedará como evento <strong>global</strong> (visible para todo el equipo). Los
            recordatorios personales se crearán desde el calendario.
          </p>

          {save.isError && (
            <div className="alert alert-error text-sm">
              {save.error instanceof Error ? save.error.message : "Error al guardar"}
            </div>
          )}

          <div className="modal-action">
            <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-sm">
              {isSubmitting && <span className="loading loading-spinner loading-xs" />}
              {isEdit ? "Guardar cambios" : "Crear evento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Recurrence fields ────────────────────────────────────────────────────

const RECURRENCE_LABEL: Record<(typeof CALENDAR_RECURRENCES)[number] | "none", string> = {
  none: "Una sola vez",
  daily: "Diaria",
  weekly: "Semanal",
  biweekly: "Cada 2 semanas",
  monthly: "Mensual",
  yearly: "Anual",
};

const DAY_LABELS = ["L", "M", "X", "J", "V", "S", "D"];

function RecurrenceFields({
  recurrence,
  recurrenceDays,
  recurrenceUntil,
  onRecurrenceChange,
  onDaysChange,
  onUntilChange,
}: {
  recurrence: (typeof CALENDAR_RECURRENCES)[number] | null | undefined;
  recurrenceDays: number[] | null | undefined;
  recurrenceUntil: string | null | undefined;
  onRecurrenceChange: (v: (typeof CALENDAR_RECURRENCES)[number] | null) => void;
  onDaysChange: (days: number[] | null) => void;
  onUntilChange: (v: string) => void;
}) {
  const days = recurrenceDays ?? [];
  const showDayPicker =
    recurrence === "daily" || recurrence === "weekly" || recurrence === "biweekly";

  const toggleDay = (d: number) => {
    if (days.includes(d)) {
      const next = days.filter((x) => x !== d);
      onDaysChange(next.length === 0 ? null : next);
    } else {
      onDaysChange([...days, d].toSorted((a, b) => a - b));
    }
  };

  return (
    <div className="space-y-2 p-3 rounded-md border border-base-300/40 bg-base-200/20">
      <div>
        <label className="label py-1">
          <span className="label-text text-sm font-medium">Recurrencia</span>
        </label>
        <select
          className="select select-bordered select-sm w-full"
          value={recurrence ?? "none"}
          onChange={(e) => {
            const v = e.target.value;
            onRecurrenceChange(v === "none" ? null : (v as (typeof CALENDAR_RECURRENCES)[number]));
          }}
        >
          {(["none", ...CALENDAR_RECURRENCES] as const).map((r) => (
            <option key={r} value={r}>
              {RECURRENCE_LABEL[r]}
            </option>
          ))}
        </select>
      </div>

      {showDayPicker && (
        <div>
          <label className="label py-1">
            <span className="label-text text-xs text-base-content/70">
              {recurrence === "daily"
                ? "Días que aplica (vacío = todos los días)"
                : "Días de la semana"}
            </span>
          </label>
          <div className="flex gap-1">
            {DAY_LABELS.map((label, idx) => {
              const selected = days.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleDay(idx)}
                  className={
                    "size-8 rounded text-xs font-semibold transition-colors " +
                    (selected
                      ? "bg-primary text-primary-content"
                      : "bg-base-100 border border-base-300/60 hover:border-primary/60")
                  }
                >
                  {label}
                </button>
              );
            })}
            {recurrence === "daily" &&
              days.length === 5 &&
              [0, 1, 2, 3, 4].every((d) => days.includes(d)) && (
                <span className="text-xs text-base-content/55 self-center ml-1 italic">
                  (entre semana)
                </span>
              )}
          </div>
        </div>
      )}

      {recurrence && (
        <div>
          <label className="label py-1">
            <span className="label-text text-xs text-base-content/70">
              Hasta (opcional — vacío = indefinido)
            </span>
          </label>
          <input
            type="date"
            className="input input-bordered input-sm w-full"
            value={recurrenceUntil ?? ""}
            onChange={(e) => onUntilChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Google recurring picker ──────────────────────────────────────────────

type GoogleRecurringItem = {
  id: string;
  summary: string;
  startDate: string;
  startTime: string | null;
  endTime: string | null;
  meetUrl: string | null;
  htmlLink: string | null;
  rrule: string | null;
  icalUid: string | null;
  mapped: {
    recurrence: CalendarRecurrence;
    recurrenceDays: number[] | null;
    recurrenceUntil: string | null;
  } | null;
};

const RRULE_HUMAN: Record<CalendarRecurrence, string> = {
  daily: "Diaria",
  weekly: "Semanal",
  biweekly: "Cada 2 sem.",
  monthly: "Mensual",
  yearly: "Anual",
};

function GoogleRecurringPicker({
  value,
  onSelect,
}: {
  value: string | null;
  onSelect: (item: GoogleRecurringItem) => void;
}) {
  const { data, isLoading, isError, error } = useQuery<{
    email: string;
    items: GoogleRecurringItem[];
  }>({
    queryKey: ["calendar-events", "google-recurring"],
    queryFn: () => api.get("/calendar-events/google-recurring"),
    // No reintentar agresivamente: si el SA no tiene scope, falla con 503
    // y queremos mostrar el detalle directo al usuario.
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-base-content/55">
        <span className="loading loading-spinner loading-xs" /> Cargando series de Google…
      </div>
    );
  }
  if (isError) {
    const msg = error instanceof Error ? error.message : "No se pudieron cargar las series";
    return (
      <div className="mt-3 alert alert-warning text-xs py-2">
        {msg}. Verifica que el Service Account tenga el scope <code>calendar.events</code> en
        Domain-wide delegation.
      </div>
    );
  }
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="mt-3 text-xs text-base-content/55">
        No se encontraron eventos recurrentes en tu calendario primario.
      </p>
    );
  }
  return (
    <div className="mt-3">
      <label className="label py-1">
        <span className="label-text text-xs text-base-content/70">Selecciona la serie maestra</span>
      </label>
      <select
        className="select select-bordered select-sm w-full"
        value={value ?? ""}
        onChange={(e) => {
          const selected = items.find((it) => it.id === e.target.value);
          if (selected) onSelect(selected);
        }}
      >
        <option value="">— Elegir —</option>
        {items.map((it) => {
          const label = it.mapped
            ? `${it.summary} · ${RRULE_HUMAN[it.mapped.recurrence]}${
                it.startTime ? ` ${it.startTime}` : ""
              }`
            : `${it.summary} · (regla no soportada)`;
          return (
            <option key={it.id} value={it.id} disabled={!it.mapped}>
              {label}
            </option>
          );
        })}
      </select>
      <p className="text-xs text-base-content/55 mt-1">
        Las series con reglas exóticas (intervalos &gt; 2, "primer lunes del mes", etc.) aparecen
        deshabilitadas. Para esas conviene crear el evento manual sin anclaje.
      </p>
    </div>
  );
}
